import { objectSpread2 as _objectSpread2, inherits as _inherits, createSuper as _createSuper, classCallCheck as _classCallCheck, createClass as _createClass, defineProperty as _defineProperty, assertThisInitialized as _assertThisInitialized, toConsumableArray as _toConsumableArray, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _pt from '../../node_modules/prop-types/index.js';
import React from 'react';
import { FormChangeSource } from './constants.js';
import { shouldFieldUpdate, omitProps, fieldFormState, shallowCompareArrays } from './utils.js';
import FormContext$1 from './FormContext.js';

var _excluded = ["field", "showErrors", "allErrors", "animateErrors", "disabled", "readOnly", "customErrors", "customValidator", "notifiedFields", "forwardedRef"];

var getDefaultOptions = function getDefaultOptions() {
  return {
    defaultProps: {},
    displayName: function displayName(props) {
      return props.field;
    },
    validators: []
  };
};

var connectForm = function connectForm() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getDefaultOptions();
  return function (FieldComponent) {
    var _options$propagateUpd;

    var defaultOptions = getDefaultOptions();
    var validators = options.validators || defaultOptions.validators || [];
    var validatorPropTypes = validators.reduce(function (acc, v) {
      return v.propTypes ? _objectSpread2(_objectSpread2({}, acc), v.propTypes) : acc;
    }, {});
    var validatorDefaultProps = validators.reduce(function (acc, v) {
      return v.defaultProps ? _objectSpread2(_objectSpread2({}, acc), v.defaultProps) : acc;
    }, {});
    var fieldDefaultProps = options.defaultProps || defaultOptions.defaultProps;
    var propagateUpdates = (_options$propagateUpd = options.propagateUpdates) !== null && _options$propagateUpd !== void 0 ? _options$propagateUpd : defaultOptions.propagateUpdates;

    var FieldProvider = /*#__PURE__*/function (_React$Component) {
      _inherits(FieldProvider, _React$Component);

      var _super = _createSuper(FieldProvider);

      function FieldProvider() {
        _classCallCheck(this, FieldProvider);

        return _super.apply(this, arguments);
      }

      _createClass(FieldProvider, [{
        key: "shouldComponentUpdate",
        value: function shouldComponentUpdate(nextProps) {
          return shouldFieldUpdate(this.props, nextProps, propagateUpdates);
        }
      }, {
        key: "render",
        value: function render() {
          return /*#__PURE__*/React.createElement(FieldComponent, this.props);
        }
      }]);

      return FieldProvider;
    }(React.Component);

    _defineProperty(FieldProvider, "propTypes", {
      value: _pt.any,
      onChange: _pt.func.isRequired,
      errors: _pt.arrayOf(_pt.string),
      passThruProps: _pt.any.isRequired,
      formState: _pt.any.isRequired,
      forwardedRef: _pt.oneOfType([_pt.string, _pt.func, _pt.object])
    });

    _defineProperty(FieldProvider, "defaultProps", {
      value: undefined
    });

    _defineProperty(FieldProvider, "displayName", "".concat(FieldComponent.displayName || FieldComponent.name, "Provider"));

    var FieldWrapper = /*#__PURE__*/function (_React$Component2) {
      _inherits(FieldWrapper, _React$Component2);

      var _super2 = _createSuper(FieldWrapper);

      function FieldWrapper() {
        var _this;

        _classCallCheck(this, FieldWrapper);

        for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
          args[_key] = arguments[_key];
        }

        _this = _super2.call.apply(_super2, [this].concat(args));

        _defineProperty(_assertThisInitialized(_this), "_prevContext", void 0);

        _defineProperty(_assertThisInitialized(_this), "onChange", function (data, additionalFormData, metadata) {
          var field = _this.props.field;

          var applyChanges = function applyChanges(prevValue) {
            if (prevValue && prevValue.hasOwnProperty(field) && prevValue[field] === data && !additionalFormData) {
              return prevValue;
            }

            return _objectSpread2(_objectSpread2(_objectSpread2({}, prevValue), additionalFormData), {}, _defineProperty({}, field, data));
          };

          if (_this.context.onChange) {
            _this.context.onChange(field, data, applyChanges, metadata !== null && metadata !== void 0 ? metadata : {
              source: FormChangeSource.Field
            });
          }
        });

        _defineProperty(_assertThisInitialized(_this), "validate", function (props, context) {
          var _props$customValidato;

          var field = props.field;
          var contextValue = context.value || {};
          var fieldValue = contextValue[field];
          var fieldName = options.displayName ? options.displayName(props) : defaultOptions.displayName ? defaultOptions.displayName(props) : "";
          var customErrors = props.customValidator ? (_props$customValidato = props.customValidator(fieldName, fieldValue, props, context)) !== null && _props$customValidato !== void 0 ? _props$customValidato : [] : [];
          var errors = validators.reduce(function (acc, v) {
            if (!v.getErrors) return acc;
            if (v.shouldValidate && !v.shouldValidate(props, context)) return acc;
            return [].concat(_toConsumableArray(acc), _toConsumableArray(v.getErrors(fieldName, fieldValue, props, context)));
          }, customErrors);

          _this.updateErrors(field, errors);
        });

        _defineProperty(_assertThisInitialized(_this), "updateErrors", function (field, errors) {
          var applyErrors = function applyErrors(prevErrors) {
            var hasOldErrors = !!prevErrors && prevErrors.hasOwnProperty(field);
            var oldErrors = hasOldErrors ? prevErrors[field] : [];

            if (oldErrors === errors || Array.isArray(oldErrors) && shallowCompareArrays(oldErrors, errors)) {
              return prevErrors;
            }

            return _objectSpread2(_objectSpread2({}, prevErrors), {}, _defineProperty({}, field, errors));
          };

          if (_this.context.onError) {
            _this.context.onError(field, errors, applyErrors);
          }
        });

        return _this;
      }

      _createClass(FieldWrapper, [{
        key: "componentDidMount",
        value: function componentDidMount() {
          this.validate(this.props, this.context);
          this._prevContext = this.context;
        }
      }, {
        key: "componentWillUnmount",
        value: function componentWillUnmount() {
          // clear validation errors so unmounted components don't invalidate forms
          var field = this.props.field;
          this.updateErrors(field, []);
        }
      }, {
        key: "componentDidUpdate",
        value: function componentDidUpdate(prevProps) {
          var _this$_prevContext,
              _this2 = this;

          var currentContextValue = this.context.value || {};
          var prevContextValue = ((_this$_prevContext = this._prevContext) === null || _this$_prevContext === void 0 ? void 0 : _this$_prevContext.value) || {};
          var validate = currentContextValue[this.props.field] !== prevContextValue[prevProps.field];

          if (!validate) {
            // run validators if any validator props have changed
            var validatorPropKeys = Object.keys(validatorPropTypes);
            validate = validatorPropKeys.some(function (k) {
              return _this2.props[k] !== prevProps[k];
            });
          }

          if (!validate) {
            // run validators if need notification of other field changes
            var notifiedFields = validators.reduce(function (acc, v) {
              if (!v.getNotifiedFields) return acc;
              return [].concat(_toConsumableArray(acc), _toConsumableArray(v.getNotifiedFields(_this2.props)));
            }, this.props.notifiedFields || []);
            validate = notifiedFields.some(function (f) {
              return currentContextValue[f] !== prevContextValue[f];
            });
          }

          if (validate) {
            this.validate(this.props, this.context);
          }

          this._prevContext = this.context;
        }
      }, {
        key: "render",
        value: function render() {
          var _this$props = this.props,
              field = _this$props.field,
              showErrors = _this$props.showErrors,
              allErrors = _this$props.allErrors,
              animateErrors = _this$props.animateErrors,
              disabled = _this$props.disabled,
              readOnly = _this$props.readOnly,
              customErrors = _this$props.customErrors;
          _this$props.customValidator;
          _this$props.notifiedFields;
          _this$props.forwardedRef;

          var otherProps = _objectWithoutProperties(_this$props, _excluded);

          var contextValue = this.context.value || {};
          var contextErrors = this.context.errors || {};
          var passThruProps = omitProps(otherProps, Object.keys(FieldWrapper.propTypes) // omits validator props
          );
          var fieldErrors = contextErrors[field];

          if (customErrors && customErrors.length) {
            fieldErrors = customErrors.concat(fieldErrors || []);
          }

          var formState = fieldFormState(this.context);
          return /*#__PURE__*/React.createElement(FieldProvider, _extends({}, this.props, {
            value: contextValue[field],
            onChange: this.onChange,
            errors: fieldErrors,
            showErrors: showErrors !== undefined ? showErrors : this.context.showErrors,
            allErrors: allErrors !== undefined ? allErrors : this.context.allErrors,
            animateErrors: animateErrors !== undefined ? animateErrors : this.context.animateErrors,
            disabled: disabled !== undefined ? disabled : this.context.disabled,
            readOnly: readOnly !== undefined ? readOnly : this.context.readOnly,
            passThruProps: passThruProps,
            formState: formState
          }));
        }
      }]);

      return FieldWrapper;
    }(React.Component);

    _defineProperty(FieldWrapper, "propTypes", _objectSpread2({}, validatorPropTypes));

    _defineProperty(FieldWrapper, "defaultProps", _objectSpread2(_objectSpread2({}, fieldDefaultProps), validatorDefaultProps));

    _defineProperty(FieldWrapper, "contextType", FormContext$1);

    _defineProperty(FieldWrapper, "displayName", "connectForm(".concat(FieldComponent.displayName || FieldComponent.name, ")"));

    var forwardRef = function forwardRef(props, ref) {
      return /*#__PURE__*/React.createElement(FieldWrapper, _extends({}, props, {
        forwardedRef: ref
      }));
    };

    forwardRef.displayName = "forwardRef(connectForm(".concat(FieldComponent.displayName || FieldComponent.name, "))");
    return /*#__PURE__*/React.forwardRef(forwardRef);
  };
};

var connectForm$1 = connectForm;

export { connectForm$1 as default };
